import CronGenerator from 'components/CronGenerator/CronGenerator';
import Text from 'components/Text/Text';
import { AppContext, IAppContext } from 'appState/appContext';
import ArticleField from './components/ArticleField';
import PosNameField from './components/PosNameField';
import { convertUndefinedFields } from 'utils/helpers';
import ActivationScheduleInput from './components/ActivationScheduleInput';
import DeactivationScheduleInput from './components/DeactivationScheduleInput';
import React, { useContext, useEffect } from 'react';
import { ServerErrorTypes, TFileId, IRestaurantForCreateDish } from 'types';
import axios from 'axios';
import { Form } from 'antd';
import CustomerField from './components/CustomerField';
import BtnBlock from './components/BtnBlock';
import DishNameField from './components/DishNameField';
import DescriptionField from './components/DescriptionField';
import IsDisabledField from './components/IsDisabledField/IsDisabledField';
import SuggestedDishesField from './components/SuggestedDishesField/SuggestedDishesField';
import AdditionalDishesField from './components/AdditionalIngredientsField/AdditionalDishesField';
import DishModificationsField from './components/DishModificationsField/DishModificationsField';
import ExternalIdField from './components/ExternalIdField';
import Button from 'components/Button/Button';
import ComboModifierSelect from './components/ComboModifierSelect';
import RestaurantsField from './components/RestaurantsField/RestaurantsField';
import CategoriesList from './components/CategoriesList';
import SubcategoriesList from './components/SubcategoriesList';
import { IsProtectedFromSync } from './components/IsProtectedFromSync';
import { TaxPercent } from './components/TaxPercent';
import { HasOpenPrice } from './components/HasOpenPrice';
import { OpenPrice } from './components/OpenPrice';
import { OpenPriceMaxThreshold } from './components/OpenPriceMaxThreshold';
import { IsFiscalized } from './components/IsFiscalized';
import ModifierGroupsField from './components/ModifierGroupsField/ModifierGroupsField';
import RestaurantsForComments from './components/RestaurantsForComments/RestaurantsForComments';
import { EditDishContext, IEditDishContext } from '../../../../../editDishState/editDishContext';
import {
	IWithAdditionalValidationContext,
	WithAdditionalValidationContext,
} from 'components/WithAdditionalValidation/withAdditionalValidationContext';
import { updateDish } from 'backend/dishes';
import DOMPurify from 'dompurify';
import { getFileIdForSubmit, preventDefaultForEnterKey } from 'utils/helpers';
import classNames from 'classnames';
import style from './InputFields.module.scss';

export const InputFields: React.FC = () => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { setMainFormValidationFail, additionalValidationErrors } = useContext<IWithAdditionalValidationContext>(
		WithAdditionalValidationContext,
	);
	const {
		changeTypeOfModalWindow,
		showErrorModalWithInfo,
		changeDishName,
		changeShortDescription,
		setComboModifier,
		updateComboModifier,
		setFetchedComboModifier,
		setRestaurantsForPrice,
		setFetchedRestaurants,
		changePrice,
		addPrices,
		removePrice,
		setRestaurantWithTurnOffComment,
		setRestaurantWithRequiredComment,
		setDisabledInRestaurants,
		setModifierGroups,
		setModifierGroupsAsAdditional,
		setFetchedModifierGroups,
		setSuggestedDishCategory,
		setFetchedCategories,
		setSuggestedDishSubCategory,
		setFetchedSuggestedDishSubCategories,
		setSuggestedDishes,
		setFetchedSuggestedDishes,
		setAdditionalDishCategory,
		setAdditionalDishSubCategory,
		setFetchedAdditionalDishSubCategories,
		setAdditionalDishes,
		setFetchedAdditionalDishes,
		setDishModificationCategory,
		setDishModificationSubCategory,
		setFetchedDishModificationSubCategories,
		setDishModifications,
		setFetchedDishModifications,
		setIDsShowInComboDishes,
		changeExternalId,
		generateUniqueExternalId,
		removeSuggestedDish,
		removeAdditionalDish,
		removeDishModification,
		changeActivationSchedule,
		changeDeactivationSchedule,
		changeLowerLimit,
		changeUpperLimit,
		toggleLimitsProtectedFromSync,
		toggleTaxPercentProtectedFromSync,
		changeTaxPercent,
		toggleFiscalized,
		toggleOpenPrice,
		changeOpenPrice,
		changeOpenPriceMaxThreshold,
		dishId,
		fileId,
		filePath,
		smallFileId,
		smallFilePath,
		videoFileId,
		videoPath,
		isVideoActive,
		name,
		shortDescription,
		posName,
		isComboDish,
		selectedComboModifier,
		selectedCustomer,
		articleNumber,
		restaurantsForPrice,
		price,
		totalPrices,
		restaurantWithTurnOffComment,
		restaurantWithRequiredComment,
		externalId,
		isCreatedInPos,
		isDisabledInRestaurants,
		translations,
		activationSchedule,
		deactivationSchedule,
		selectedModifierGroups,
		modifierGroupsWithAdditionalData,
		selectedModifierGroupsAsAdditional,
		selectedSuggestedDishCategory,
		selectedSuggestedDishSubCategory,
		selectedSuggestedDishes,
		selectedAdditionalDishCategory,
		selectedAdditionalDishSubCategory,
		selectedAdditionalDishes,
		selectedDishModificationCategory,
		selectedDishModificationSubCategory,
		selectedDishModifications,
		IDsShowInComboDishes,
		fetchedRestaurants,
		fetchedDishModifications,
		fetchedSuggestedDishes,
		fetchedAdditionalDishes,
		fetchedModifierGroups,
		initialCategories,
		initialSubCategories,
		initialModifierGroups,
		initialModifierGroupsAsAdditional,
		initialSelectedComboModifier,
		defaultCustomerLanguage,
		isTaxPercentProtectedFromSync,
		taxPercent,
		hasOpenPrice,
		openPrice,
		openPriceMaxThreshold,
		isFiscalized,
	} = useContext<IEditDishContext>(EditDishContext);
	const [form] = Form.useForm();
	const restaurantCommentsStyles = classNames(style.row, style.flexStart);

	useEffect(() => {
		form.setFieldsValue({
			name,
			posName,
			selectedCustomer,
			articleNumber,
			selectedComboModifier,
			restaurantsForPrice,
			price,
			restaurantWithTurnOffComment,
			restaurantWithRequiredComment,
			externalId,
			isDisabledInRestaurants,
			activationSchedule,
			deactivationSchedule,
			selectedModifierGroups,
			selectedModifierGroupsAsAdditional,
			selectedSuggestedDishCategory,
			selectedSuggestedDishSubCategory,
			selectedSuggestedDishes,
			selectedAdditionalDishCategory,
			selectedAdditionalDishSubCategory,
			selectedAdditionalDishes,
			selectedDishModificationCategory,
			selectedDishModificationSubCategory,
			selectedDishModifications,
			isTaxPercentProtectedFromSync,
			taxPercent,
			hasOpenPrice,
			openPrice,
			openPriceMaxThreshold,
			isFiscalized,
		});
		modifierGroupsWithAdditionalData.forEach((el) => {
			form.setFieldsValue({
				[`upperLimit-${el.modifierGroupId}`]: el.upperLimit,
				[`lowerLimit-${el.modifierGroupId}`]: el.lowerLimit,
				[`isLimitsProtectedFromSync-${el.modifierGroupId}`]: el.isLimitsProtectedFromSync,
			});
		});
	}, [
		name,
		posName,
		selectedComboModifier,
		selectedCustomer,
		articleNumber,
		externalId,
		restaurantsForPrice,
		price,
		restaurantWithTurnOffComment,
		restaurantWithRequiredComment,
		totalPrices,
		isDisabledInRestaurants,
		activationSchedule,
		deactivationSchedule,
		selectedModifierGroups,
		modifierGroupsWithAdditionalData,
		selectedModifierGroupsAsAdditional,
		selectedSuggestedDishCategory,
		selectedSuggestedDishSubCategory,
		selectedSuggestedDishes,
		selectedAdditionalDishCategory,
		selectedAdditionalDishSubCategory,
		selectedAdditionalDishes,
		selectedDishModificationCategory,
		selectedDishModificationSubCategory,
		selectedDishModifications,
		isTaxPercentProtectedFromSync,
		taxPercent,
		hasOpenPrice,
		openPrice,
		openPriceMaxThreshold,
		isFiscalized,
	]);

	const submit = async () => {
		const defaultTranslationIsEmpty =
			translations.find((translation) => translation.languageId === defaultCustomerLanguage?.id)?.name === '';
		if (defaultTranslationIsEmpty) {
			changeTypeOfModalWindow('defaultTranslationIsEmpty');
			return;
		}

		setMainFormValidationFail(false);
		if (additionalValidationErrors.length) {
			setMainFormValidationFail(true);
			return;
		}

		const finalRestaurants: IRestaurantForCreateDish[] = totalPrices.map((el) => {
			return {
				restaurantId: el.restaurantId,
				price: el.price ? Number(el.price) : 0,
				isDisabled: isDisabledInRestaurants.includes(el.restaurantId),
				isCommentTurnOff: restaurantWithTurnOffComment.includes(el.restaurantId),
				isCommentRequired: restaurantWithRequiredComment.includes(el.restaurantId),
			};
		});

		const finalSmallFile = (): TFileId =>
			isVideoActive ? null : getFileIdForSubmit({ fileId: smallFileId, filePath: smallFilePath });

		const finalVideoFile = (): TFileId =>
			isVideoActive ? getFileIdForSubmit({ fileId: videoFileId, filePath: videoPath }) : null;

		const finalDishModifications = selectedDishModifications.map((id) => ({
			modificationId: id,
			showInCombo: IDsShowInComboDishes[id],
		}));

		const finalModifierGroups = selectedModifierGroups.map((el) => {
			const groupWithAdditionalData = modifierGroupsWithAdditionalData.find(
				(additional) => additional.modifierGroupId === el,
			);

			return {
				modifierGroupId: el,
				upLimit: groupWithAdditionalData?.upperLimit ? Number(groupWithAdditionalData.upperLimit) : null,
				downLimit: groupWithAdditionalData?.lowerLimit ? Number(groupWithAdditionalData.lowerLimit) : null,
				isAdditional: selectedModifierGroupsAsAdditional.includes(el),
				isLimitsProtectedFromSync: groupWithAdditionalData?.isLimitsProtectedFromSync
					? groupWithAdditionalData.isLimitsProtectedFromSync
					: false,
			};
		});

		// Swapping `filePath` and `smallFilePath` is not an error; there was an urgent business requirement to swap them.

		try {
			changeTypeOfModalWindow(null);
			const convertedData = convertUndefinedFields({
				id: dishId,
				name,
				shortDescription: shortDescription ? DOMPurify.sanitize(shortDescription) : null,
				customerId: selectedCustomer || '',
				fileId: getFileIdForSubmit({ fileId, filePath }),
				smallFileId: finalSmallFile(),
				videoFileId: finalVideoFile(),
				activationCron: activationSchedule,
				deactivationCron: deactivationSchedule,
				dishRestaurants: finalRestaurants,
				comboModifierId: selectedComboModifier,
				modifierGroups: finalModifierGroups,
				suggestedDishIds: selectedSuggestedDishes,
				additionalDishIds: selectedAdditionalDishes,
				dishModifications: finalDishModifications,
				externalId,
				translations,
				isTaxPercentProtectedFromSync,
				taxPercent,
				hasOpenPrice,
				openPrice,
				openPriceMaxThreshold,
				isFiscalized,
			});
			showLoader();
			await updateDish(convertedData);
			changeTypeOfModalWindow('success');
		} catch (err) {
			if (
				axios.isAxiosError(err) &&
				[
					ServerErrorTypes.SUGGESTED_DISH_VALIDATION_FAILED,
					ServerErrorTypes.ADDITIONAL_DISH_VALIDATION_FAILED,
					ServerErrorTypes.DISH_MODIFICATION_VALIDATION_FAILED,
				].includes(err.response?.data.Type)
			) {
				showErrorModalWithInfo({ ...err.response?.data, errorType: err.response?.data.Type });
			}
			if (
				axios.isAxiosError(err) &&
				err.response?.data.Type === ServerErrorTypes.COMBO_MODIFIER_VALIDATION_FAILED
			) {
				changeTypeOfModalWindow('comboModifierValidationError');
			} else {
				changeTypeOfModalWindow('error');
			}
		} finally {
			hideLoader();
		}
	};

	const revalidateForm = form.validateFields;

	return (
		<div className={style.container} data-test="InputFields">
			<Form form={form} name="basic" onFinish={submit} onKeyPress={preventDefaultForEnterKey} layout="vertical">
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="mainInfoHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<DishNameField changeDishName={changeDishName} />
					<CustomerField />
					<ArticleField />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="descriptionHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<DescriptionField
						changeShortDescription={changeShortDescription}
						shortDescription={shortDescription}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="posNameLabel"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<PosNameField />
				</div>
				{!isComboDish && (
					<div className={style.fieldsWrapper}>
						<Text
							translationNameSpace="form"
							translationKey="comboModifierHeading"
							theme="secondary"
							size="l"
							weight="bold"
						/>
						<ComboModifierSelect
							setComboModifier={setComboModifier}
							updateComboModifier={updateComboModifier}
							setFetchedComboModifier={setFetchedComboModifier}
							totalPrices={totalPrices}
							initialSelectedComboModifier={initialSelectedComboModifier}
						/>
					</div>
				)}
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="restaurantsHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<RestaurantsField
						restaurantsForPrice={restaurantsForPrice}
						totalPrices={totalPrices}
						price={price}
						fetchedRestaurants={fetchedRestaurants}
						selectedCustomer={selectedCustomer}
						revalidateForm={revalidateForm}
						setFetchedRestaurants={setFetchedRestaurants}
						changePrice={changePrice}
						setRestaurantsForPrice={setRestaurantsForPrice}
						addPrices={addPrices}
						removePrice={removePrice}
						isCreatedInPos={isCreatedInPos}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="categoryAndSubcategoryHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<CategoriesList initialCategories={initialCategories} />
					<SubcategoriesList initialSubCategories={initialSubCategories} />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="taxHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<IsProtectedFromSync
						toggleTaxPercentProtectedFromSync={toggleTaxPercentProtectedFromSync}
						isTaxPercentProtectedFromSync={isTaxPercentProtectedFromSync}
					/>
					<TaxPercent
						changeTaxPercent={changeTaxPercent}
						isTaxPercentProtectedFromSync={isTaxPercentProtectedFromSync}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="openPriceHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<HasOpenPrice toggleOpenPrice={toggleOpenPrice} hasOpenPrice={hasOpenPrice} />
					{hasOpenPrice && (
						<>
							<OpenPrice changeOpenPrice={changeOpenPrice} />
							<OpenPriceMaxThreshold changeOpenPriceMaxThreshold={changeOpenPriceMaxThreshold} />
						</>
					)}
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="feeFiscalisationHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<IsFiscalized toggleFiscalized={toggleFiscalized} isFiscalized={isFiscalized} />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="idHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<div className={style.row}>
						<ExternalIdField changeExternalId={changeExternalId} />
						<Button onClick={generateUniqueExternalId} theme="primary">
							<Text
								translationNameSpace="form"
								translationKey="generateUniqueExternalId"
								asFragment={true}
							/>
						</Button>
					</div>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="inStopHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<IsDisabledField
						isDisabledInRestaurants={isDisabledInRestaurants}
						fetchedRestaurants={fetchedRestaurants}
						totalPrices={totalPrices}
						isCreatedInPos={isCreatedInPos}
						setDisabledInRestaurants={setDisabledInRestaurants}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="commentsHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<div className={restaurantCommentsStyles}>
						<RestaurantsForComments
							setIDsRestaurant={setRestaurantWithTurnOffComment}
							IDSelectedCustomer={selectedCustomer}
							IDsSelectedRestaurant={restaurantWithTurnOffComment}
							restaurantsToExclude={restaurantWithRequiredComment}
							totalPrices={totalPrices}
							inputName="restaurantWithTurnOffComment"
							inputLabel="turnOffCommentLabel"
						/>
						<RestaurantsForComments
							setIDsRestaurant={setRestaurantWithRequiredComment}
							IDSelectedCustomer={selectedCustomer}
							IDsSelectedRestaurant={restaurantWithRequiredComment}
							restaurantsToExclude={restaurantWithTurnOffComment}
							totalPrices={totalPrices}
							inputName="restaurantWithRequiredComment"
							inputLabel="requiredCommentLabel"
						/>
					</div>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="scheduleHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<div className={style.row}>
						<ActivationScheduleInput changeActivationSchedule={changeActivationSchedule} />
						<DeactivationScheduleInput changeDeactivationSchedule={changeDeactivationSchedule} />
					</div>
					<CronGenerator />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="modifierGroupsHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<ModifierGroupsField
						fetchedModifierGroups={fetchedModifierGroups}
						selectedModifierGroups={selectedModifierGroups}
						totalPrices={totalPrices}
						initialModifierGroups={initialModifierGroups}
						setModifierGroups={setModifierGroups}
						setFetchedModifierGroups={setFetchedModifierGroups}
						setModifierGroupsAsAdditional={setModifierGroupsAsAdditional}
						initialModifierGroupsAsAdditional={initialModifierGroupsAsAdditional}
						selectedModifierGroupsAsAdditional={selectedModifierGroupsAsAdditional}
						changeLowerLimit={changeLowerLimit}
						changeUpperLimit={changeUpperLimit}
						toggleLimitsProtectedFromSync={toggleLimitsProtectedFromSync}
						modifierGroupsWithAdditionalData={modifierGroupsWithAdditionalData}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="suggestedDishesHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<SuggestedDishesField
						selectedSuggestedDishes={selectedSuggestedDishes}
						fetchedSuggestedDishes={fetchedSuggestedDishes}
						selectedSuggestedDishCategory={selectedSuggestedDishCategory}
						selectedSuggestedDishSubCategory={selectedSuggestedDishSubCategory}
						totalPrices={totalPrices}
						setSuggestedDishes={setSuggestedDishes}
						removeSuggestedDish={removeSuggestedDish}
						setFetchedSuggestedDishes={setFetchedSuggestedDishes}
						setFetchedSuggestedDishSubCategories={setFetchedSuggestedDishSubCategories}
						setSuggestedDishSubCategory={setSuggestedDishSubCategory}
						setFetchedCategories={setFetchedCategories}
						setSuggestedDishCategory={setSuggestedDishCategory}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="additionalIngredientsHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<AdditionalDishesField
						selectedAdditionalDishes={selectedAdditionalDishes}
						selectedAdditionalDishCategory={selectedAdditionalDishCategory}
						selectedAdditionalDishSubCategory={selectedAdditionalDishSubCategory}
						fetchedAdditionalDishes={fetchedAdditionalDishes}
						totalPrices={totalPrices}
						removeAdditionalDish={removeAdditionalDish}
						setAdditionalDishes={setAdditionalDishes}
						setFetchedAdditionalDishes={setFetchedAdditionalDishes}
						setFetchedAdditionalDishSubCategories={setFetchedAdditionalDishSubCategories}
						setAdditionalDishSubCategory={setAdditionalDishSubCategory}
						setAdditionalDishCategory={setAdditionalDishCategory}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="dishModificationsHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<DishModificationsField
						selectedDishModifications={selectedDishModifications}
						selectedDishModificationCategory={selectedDishModificationCategory}
						selectedDishModificationSubCategory={selectedDishModificationSubCategory}
						totalPrices={totalPrices}
						fetchedDishModifications={fetchedDishModifications}
						setFetchedDishModifications={setFetchedDishModifications}
						setDishModifications={setDishModifications}
						setFetchedDishModificationSubCategories={setFetchedDishModificationSubCategories}
						setDishModificationSubCategory={setDishModificationSubCategory}
						setDishModificationCategory={setDishModificationCategory}
						removeDishModification={removeDishModification}
						setIDsShowInComboDishes={setIDsShowInComboDishes}
						IDsShowInComboDishes={IDsShowInComboDishes}
					/>
				</div>
				<BtnBlock />
			</Form>
		</div>
	);
};
